import React from 'react'
import { Controller, useFormContext, type FieldValues } from 'react-hook-form'
import { TextField } from '@diversioteam/diversio-ds'
import { get } from 'lodash'

import type { IFormTextareaProps } from './FormTextarea.types'
import * as S from './FormTextarea.styles'

export function FormTextarea<TFieldValues extends FieldValues = FieldValues>({
    name,
    rules,
    maxLength,
    showCharacterCount = false,
    minRows = 4,
    maxRows,
    maxHeight,
    ...rest
}: IFormTextareaProps<TFieldValues>) {
    const {
        control,
        formState: { errors },
        clearErrors,
    } = useFormContext<TFieldValues>()

    const hasError = !!get(errors, name)
    const errorMessage = get(errors, name)?.message as string

    return (
        <S.Container>
            <Controller
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value, name: fieldName } }) => {
                    const currentValue = value || ''
                    const currentLength = currentValue.length
                    const isOverLimit = maxLength ? currentLength > maxLength : false

                    return (
                        <>
                            <S.TextareaWrapper>
                                <TextField
                                    {...rest}
                                    name={fieldName}
                                    id={name}
                                    value={currentValue}
                                    fullWidth
                                    variant="outlined"
                                    multiline
                                    minRows={minRows}
                                    maxRows={maxRows}
                                    maxHeight={maxHeight}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                        const newValue = e.target.value

                                        // Prevent input if over max length (hard limit)
                                        if (maxLength && newValue.length > maxLength) {
                                            return
                                        }

                                        onChange(e)

                                        // Auto-clear required errors for better UX
                                        if (get(errors, name)?.type === 'required') {
                                            clearErrors(name)
                                        }
                                    }}
                                    error={hasError || isOverLimit}
                                    InputProps={{
                                        style:
                                            hasError || isOverLimit
                                                ? ({
                                                      borderColor: '#dc2626',
                                                  } as React.CSSProperties)
                                                : undefined,
                                    }}
                                />
                            </S.TextareaWrapper>

                            {showCharacterCount && maxLength && (
                                <S.CharacterCount $hasError={hasError} $isOverLimit={isOverLimit}>
                                    {currentLength}/{maxLength}
                                </S.CharacterCount>
                            )}

                            {hasError && <S.ErrorText>{errorMessage}</S.ErrorText>}
                        </>
                    )
                }}
            />
        </S.Container>
    )
}
